import type {
  HydratedConnector,
  HydratedConnectorDragonTile,
} from "@/api/connectors/index";
import type { ConnectorTile } from "@/types/connectors";

/**
 * The honeycomb layouts support at most 20 tiles, and the "Add Connector"
 * tile always occupies one slot, so installed connectors cap at 19.
 */
const DEFAULT_VISIBLE_CONNECTOR_LIMIT = 19;

/**
 * Single source of truth for whether a connector's dragon tile should be
 * shown to users. Exported so callers can filter `HydratedConnector[]`
 * directly instead of re-deriving visibility from `getVisibleConnectorDisplayTiles`'s
 * output (which remaps `id` to `connectorId` and is meant for display, not filtering).
 *
 * Narrows as it filters: a connector with no Dragon Tile (a data hub with no
 * subscribable events) is never visible on a Dragon Tiles surface, so callers
 * that pass this to `.filter` get tile-guaranteed entries back.
 */
export function isVisibleConnectorDragonTile(
  connector: HydratedConnector,
): connector is HydratedConnectorDragonTile {
  return (
    !!connector.dragonTile &&
    !!connector.tile &&
    (connector.tile.events?.length ?? 0) > 0
  );
}

export function getVisibleConnectorDisplayTiles(
  connectors: HydratedConnector[],
  limit = DEFAULT_VISIBLE_CONNECTOR_LIMIT,
): ConnectorTile[] {
  return connectors
    .filter(isVisibleConnectorDragonTile)
    .slice(0, limit)
    .map((connector) => ({
      ...connector.tile,
      id: connector.connectorId,
    }));
}

export function hasVisibleConnectorDragonTile(
  connectors: HydratedConnector[],
): boolean {
  return connectors.some(isVisibleConnectorDragonTile);
}
